import * as THREE from 'three';
import { CAP_OBSTACLES, DT, OBSTACLE_SCROLL_SPEED } from '../sim/constants';
import { Ev } from '../sim/events';
import type { World } from '../sim/World';
import { EXPLOSION } from './palette';
import { Layer } from './RenderLayers';

/* ═══════════════════════════════════════════════════════════════════════════
   Le rendu du décor COLLISIONNABLE — astéroïdes et pièces de complexe militaire.

   Vue AUTONOME, calquée sur le style de `HullFleet` mais sans rien lui emprunter :
   ses propres géométries, ses propres matériaux, son propre `InstancedMesh`. Elle
   LIT `world.obstacles` chaque frame et n'écrit jamais dans la simulation
   (PLAN.md §3.1) — le rayon, les PV et le drapeau `destructible` sont déjà dans
   le pool, la vue ne fait que les traduire en silhouette, teinte et échelle.

   ── Un seul lot d'icosaèdres facettés ──
   Une `IcosahedronGeometry` normalisée au rayon 1, à plat (`flatShading`) : le
   low-poly anguleux LIT comme du rocher, et l'échelle d'instance EST le rayon de
   l'obstacle — aucune constante magique entre la hurtbox et le dessin, exactement
   la convention de la flotte. Un `InstancedMesh` alloué UNE fois à `CAP_OBSTACLES`
   (jamais par archétype : un obstacle qui déborderait son lot serait invisible ET
   mortel, le pire défaut du genre) ; seul `count` varie, zéro allocation par
   frame.

   ── La teinte porte l'information ──
   Bleu-acier froid pour un destructible, plus sombre encore pour un
   indestructible : le joueur lit « celui-là ne se casse pas » à la seule valeur.
   Un destructible entamé se réchauffe vers l'ambre au fil de ses PV — le même
   principe HDR que la flotte (un ennemi touché s'éclaircit), transposé au décor.
   Aucune de ces teintes n'entre dans la bande danger (rouge/rose/violet), qui
   reste réservée aux projectiles ennemis (PLAN.md §4.5).

   ── L'éclatement ──
   Elle DRAINE `Ev.ObstacleBroken` (émis par `collisionSystem` quand un
   destructible tombe à zéro) et lance une gerbe d'éclats ambrés, dimensionnée sur
   le rayon transmis par l'événement. Un astéroïde qui disparaîtrait sans gerbe se
   lirait comme un bug d'affichage, pas comme une destruction. Les éclats sont un
   effet purement cosmétique : ils ne touchent jamais l'état de simulation, et
   leur avancée est cadencée sur `w.tick` — donc figée sur tout écran de pause,
   comme le reste du décor.
   ═══════════════════════════════════════════════════════════════════════════ */

const _mat = new THREE.Matrix4();
const _pos = new THREE.Vector3();
const _quat = new THREE.Quaternion();
/** Second quaternion scratch, pour composer inclinaison FIXE + bancle d'une
 *  plateforme sans allouer. */
const _tiltQ = new THREE.Quaternion();
const _scale = new THREE.Vector3();
/** Axe de culbute PROPRE à l'obstacle courant, réutilisé : zéro allocation par frame. */
const _axis = new THREE.Vector3();
const _axisZ = new THREE.Vector3(0, 0, 1);
/** Axe X, pour l'inclinaison FIXE d'une plateforme (on voit la dalle de biais). */
const _axisX = new THREE.Vector3(1, 0, 0);

/** Inclinaison fixe d'une plateforme, en radians : on la voit légèrement de
 *  dessus, ce qui lui donne son épaisseur et la distingue d'un disque plat. */
const PLATFORM_TILT = 0.3;
/** Teinte d'une plateforme : un acier tech froid, distinct du bleu-rocher, jamais
 *  dans la bande danger (§4.5). Le léger émissif du matériau la fait « respirer ». */
const TINT_PLATFORM: readonly [number, number, number] = [0.34, 0.46, 0.5];

/** Hash déterministe [0,1), le hash GLSL classique. Purement VISUEL — il ne
 *  touche jamais la simulation ; il ne sert qu'à donner à chaque rocher une
 *  culbute et une silhouette stables mais distinctes, sans stocker un état de
 *  plus par obstacle côté sim. */
function hash01(n: number): number {
  const x = Math.sin(n * 12.9898) * 43758.5453;
  return x - Math.floor(x);
}

/** Teinte de base d'un rocher destructible (bleu-acier froid). */
const TINT_DESTRUCTIBLE: readonly [number, number, number] = [0.44, 0.52, 0.6];
/** Teinte de base d'un indestructible : plus sombre, la solidité se lit. */
const TINT_INDESTRUCTIBLE: readonly [number, number, number] = [0.26, 0.32, 0.4];
/** Ambre vers lequel se réchauffe un destructible entamé (famille explosion). */
const TINT_WEAR: readonly [number, number, number] = [1.0, 0.56, 0.24];

/** Éclats par brisement, et leur durée de vie. Assez pour lire une gerbe, assez
 *  court pour ne pas encombrer l'écran. */
const SHARDS_PER_BREAK = 7;
const SHARD_LIFE = 0.5;
/** Capacité du lot d'éclats : plusieurs brisements peuvent se chevaucher. Au-delà,
 *  le plus ancien est écrasé (anneau) — un éclat perdu est invisible, jamais une
 *  faute. */
const SHARD_CAP = 192;

export class ObstacleView {
  private readonly rocks: THREE.InstancedMesh;
  /** Les PLATEFORMES : un lot séparé, dalle plate au lieu de rocher culbutant. Un
   *  obstacle `platform` s'y dessine ; tous les autres restent des rochers. */
  private readonly platforms: THREE.InstancedMesh;
  private readonly shards: THREE.InstancedMesh;

  /* Culbute et silhouette PROPRES à chaque slot, tirées une fois au montage. Un
     rocher ne tourne pas comme son voisin (axe et vitesse de rotation distincts)
     et n'a pas la même forme (échelle non-uniforme) : le champ d'astéroïdes cesse
     d'être un vol de clones. Indexées par SLOT, donc stables tant que l'obstacle
     vit ; un slot recyclé hérite d'une nouvelle culbute, ce qui est invisible.

     Côté SIMULATION, un obstacle reste un disque de rayon `r` : cette variété est
     strictement cosmétique, et l'échelle non-uniforme reste modérée (0,8–1,2) pour
     que la silhouette ne s'écarte jamais assez du disque de collision pour se
     sentir injuste. */
  private readonly axis = new Float32Array(CAP_OBSTACLES * 3);
  private readonly spinRate = new Float32Array(CAP_OBSTACLES);
  private readonly jitter = new Float32Array(CAP_OBSTACLES * 3);

  /* Les éclats en vol, en Structure-of-Arrays comme les pools de simulation :
     alloués une fois, jamais reconstruits. */
  private readonly sx = new Float32Array(SHARD_CAP);
  private readonly sy = new Float32Array(SHARD_CAP);
  private readonly svx = new Float32Array(SHARD_CAP);
  private readonly svy = new Float32Array(SHARD_CAP);
  private readonly ssize = new Float32Array(SHARD_CAP);
  private readonly slife = new Float32Array(SHARD_CAP);
  private readonly sang = new Float32Array(SHARD_CAP);
  private readonly svr = new Float32Array(SHARD_CAP);
  private shardHead = 0;

  /** Dernier tick vu, pour dériver le pas de temps de l'animation des éclats
   *  depuis l'horloge de simulation — figée sur toute pause. */
  private lastTick = -1;

  private readonly ember = new THREE.Color(EXPLOSION[0]);

  constructor(scene: THREE.Scene) {
    // ── Culbute et silhouette par slot, tirées une fois ──────────────────────
    for (let s = 0; s < CAP_OBSTACLES; s++) {
      // Axe de rotation quelconque en 3D (pas un simple spin à plat) : c'est ce
      // qui donne l'aspect « météore qui culbute » plutôt que « pièce qui tourne ».
      let ax = hash01(s * 3 + 1) - 0.5;
      let ay = hash01(s * 3 + 2) - 0.5;
      let az = hash01(s * 3 + 3) - 0.5;
      const len = Math.hypot(ax, ay, az) || 1;
      ax /= len;
      ay /= len;
      az /= len;
      this.axis[s * 3] = ax;
      this.axis[s * 3 + 1] = ay;
      this.axis[s * 3 + 2] = az;
      // Vitesse de rotation propre, signe aléatoire : certains tournent à l'envers.
      const speed = 0.4 + hash01(s * 7.13) * 1.5;
      this.spinRate[s] = hash01(s * 11.7) < 0.5 ? -speed : speed;
      // Échelle non-uniforme modérée : allonge/écrase le rocher différemment sur
      // chaque axe, ce qui varie la silhouette sans trop s'éloigner du disque.
      this.jitter[s * 3] = 0.8 + hash01(s * 5.2 + 0.1) * 0.4;
      this.jitter[s * 3 + 1] = 0.8 + hash01(s * 5.2 + 0.2) * 0.4;
      this.jitter[s * 3 + 2] = 0.8 + hash01(s * 5.2 + 0.3) * 0.4;
    }

    // Rayon 1, un cran de subdivision, PUIS déplacement des sommets pour un caillou
    // craquelé plutôt qu'un icosaèdre régulier. Le déplacement est indexé par
    // POSITION (et non par sommet) pour que les coins partagés bougent ensemble —
    // la maille reste soudée, seul le relief change. Avec `flatShading`, chaque
    // facette se rééclaire, et le rocher lit comme de la pierre.
    const rockGeo = new THREE.IcosahedronGeometry(1, 1);
    const rp = rockGeo.attributes.position as THREE.BufferAttribute;
    for (let i = 0; i < rp.count; i++) {
      const vx = rp.getX(i);
      const vy = rp.getY(i);
      const vz = rp.getZ(i);
      // Clé stable par position : deux sommets au même point donnent le même
      // facteur, donc les faces ne se déchirent pas.
      const key =
        Math.round(vx * 32) * 131.1 + Math.round(vy * 32) * 57.7 + Math.round(vz * 32) * 19.3;
      const f = 0.78 + hash01(key) * 0.42; // relief radial 0,78–1,20
      rp.setXYZ(i, vx * f, vy * f, vz * f);
    }
    rockGeo.computeVertexNormals();

    const rockMat = new THREE.MeshStandardMaterial({
      color: 0xffffff, // la teinte vient de la couleur d'instance
      metalness: 0.08,
      roughness: 0.9,
      flatShading: true,
    });
    this.rocks = new THREE.InstancedMesh(rockGeo, rockMat, CAP_OBSTACLES);
    this.rocks.frustumCulled = false;
    this.rocks.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    // Une couleur d'instance posée d'emblée : sans elle, `USE_INSTANCING_COLOR`
    // manque et un premier obstacle teinté recompilerait le programme en plein
    // jeu — le même piège que sur les drones et la flotte.
    this.rocks.instanceColor = new THREE.InstancedBufferAttribute(
      new Float32Array(CAP_OBSTACLES * 3).fill(1),
      3,
    );
    this.rocks.instanceColor.setUsage(THREE.DynamicDrawUsage);
    this.rocks.count = 0;
    // Terrain : juste sous les ennemis, qui doivent se détacher devant lui.
    this.rocks.renderOrder = Layer.Enemies - 1;
    scene.add(this.rocks);

    /* ── Les PLATEFORMES ──────────────────────────────────────────────────────
       Une dalle HEXAGONALE plate, légèrement biseautée (radiusBottom < radiusTop)
       et épaisse, tournée pour présenter sa FACE au joueur : elle lit comme une
       structure mécanique, pas comme de la roche. Le matériau a un léger émissif
       teal — une plateforme est « alimentée », elle respire ; assez faible pour ne
       pas franchir le seuil de bloom (0,85). Un second `InstancedMesh`, un second
       matériau : bornés, primés au warm-up. */
    const platGeo = new THREE.CylinderGeometry(1, 0.9, 0.5, 6, 1);
    platGeo.rotateX(Math.PI / 2); // face de l'hexagone vers la caméra (Z)
    const platMat = new THREE.MeshStandardMaterial({
      color: 0xffffff, // la teinte vient de la couleur d'instance
      metalness: 0.35,
      roughness: 0.55,
      emissive: new THREE.Color(0.04, 0.11, 0.12),
      flatShading: true,
    });
    this.platforms = new THREE.InstancedMesh(platGeo, platMat, CAP_OBSTACLES);
    this.platforms.frustumCulled = false;
    this.platforms.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.platforms.instanceColor = new THREE.InstancedBufferAttribute(
      new Float32Array(CAP_OBSTACLES * 3).fill(1),
      3,
    );
    this.platforms.instanceColor.setUsage(THREE.DynamicDrawUsage);
    this.platforms.count = 0;
    this.platforms.renderOrder = Layer.Enemies - 1;
    scene.add(this.platforms);

    // Les éclats : de petits fragments non éclairés, ambrés, qui traversent le
    // bloom de la scène principale et brillent donc comme une gerbe.
    const shardGeo = new THREE.IcosahedronGeometry(1, 0);
    const shardMat = new THREE.MeshBasicMaterial({ color: 0xffffff });
    this.shards = new THREE.InstancedMesh(shardGeo, shardMat, SHARD_CAP);
    this.shards.frustumCulled = false;
    this.shards.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.shards.instanceColor = new THREE.InstancedBufferAttribute(
      new Float32Array(SHARD_CAP * 3).fill(1),
      3,
    );
    this.shards.instanceColor.setUsage(THREE.DynamicDrawUsage);
    this.shards.count = 0;
    this.shards.renderOrder = Layer.Pickups;
    scene.add(this.shards);
  }

  /**
   * Une instance hors champ dans chaque lot, le temps du warm-up.
   *
   * ⚠️ Un `InstancedMesh` à `count = 0` est purement ignoré par le renderer :
   * son programme ne serait pas compilé, et les deux matériaux neufs de cette vue
   * (rocher éclairé, éclat non éclairé) se compileraient au premier obstacle
   * apparu — c'est-à-dire en plein champ d'astéroïdes. On les rend visibles au
   * warm-up, comme la flotte et les projectiles.
   */
  primeForWarmup(): void {
    const FAR = 100000;
    _pos.set(FAR, FAR, 0);
    _quat.identity();
    _scale.setScalar(1);
    _mat.compose(_pos, _quat, _scale);

    this.rocks.setMatrixAt(0, _mat);
    this.rocks.count = 1;
    this.rocks.instanceMatrix.needsUpdate = true;

    this.platforms.setMatrixAt(0, _mat);
    this.platforms.count = 1;
    this.platforms.instanceMatrix.needsUpdate = true;

    this.shards.setMatrixAt(0, _mat);
    this.shards.count = 1;
    this.shards.instanceMatrix.needsUpdate = true;
  }

  /** `alpha` ∈ [0,1) : fraction du tick restant à interpoler, comme pour les
   *  ennemis. Le décor est interpolé (contrairement au joueur) : un tick de
   *  latence visuelle sur un rocher ne se sent pas, et la fluidité, si. */
  sync(w: World, alpha: number): void {
    // Pas de temps de l'animation, dérivé de l'horloge de SIMULATION. Sur une
    // pause dure, `w.tick` ne bouge pas → dt nul → les éclats se figent avec le
    // reste, sans code de pause dédié.
    const dTicks = this.lastTick < 0 ? 0 : w.tick - this.lastTick;
    this.lastTick = w.tick;
    const dt = dTicks > 0 ? dTicks * DT : 0;

    this.drainBreaks(w);
    this.syncObstacles(w, alpha);
    this.syncShards(dt);
  }

  /** Draine les brisements du tick et sème une gerbe par obstacle détruit. */
  private drainBreaks(w: World): void {
    const ev = w.events;
    for (let i = 0; i < ev.count; i++) {
      if (ev.type(i) !== Ev.ObstacleBroken) continue;
      // Convention de l'événement : b/c = position, d = rayon.
      this.burst(ev.b(i), ev.c(i), ev.d(i));
    }
  }

  private burst(x: number, y: number, radius: number): void {
    // Angles répartis régulièrement, décalés d'un burst à l'autre pour que deux
    // gerbes voisines ne se calquent pas. Purement cosmétique : aucun RNG de
    // gameplay, la vue a le droit d'être approximative.
    const base = (this.shardHead % SHARDS_PER_BREAK) * 0.9;
    for (let k = 0; k < SHARDS_PER_BREAK; k++) {
      const s = this.shardHead % SHARD_CAP;
      this.shardHead++;
      const ang = base + (k / SHARDS_PER_BREAK) * Math.PI * 2;
      const speed = 70 + radius * 2.2 + (k % 3) * 26;
      this.sx[s] = x;
      this.sy[s] = y;
      this.svx[s] = Math.cos(ang) * speed;
      // Les éclats héritent en partie du défilement du monde : ils retombent avec
      // le champ au lieu de flotter, ce qui les rattache au terrain.
      this.svy[s] = Math.sin(ang) * speed - OBSTACLE_SCROLL_SPEED * 0.5;
      this.ssize[s] = radius * (0.16 + (k % 4) * 0.05);
      this.slife[s] = SHARD_LIFE;
      this.sang[s] = ang;
      this.svr[s] = 4 + (k % 5);
    }
  }

  private syncObstacles(w: World, alpha: number): void {
    const ob = w.obstacles;
    const defs = w.defs.obstacles;
    // Horloge de SIMULATION : sur une pause dure, `w.tick` ne bouge pas et la
    // culbute se fige avec le reste, sans code de pause dédié.
    const t = w.tick * DT;
    const rockCol = this.rocks.instanceColor!;
    const platCol = this.platforms.instanceColor!;
    let nRock = 0;
    let nPlat = 0;

    for (let i = 0; i < ob.count; i++) {
      const s = ob.at(i);
      const x = ob.px[s]! + (ob.x[s]! - ob.px[s]!) * alpha;
      const y = ob.py[s]! + (ob.y[s]! - ob.py[s]!) * alpha;
      const r = ob.r[s]!;
      _pos.set(x, y, 0);

      if (defs[ob.type[s]!]?.platform) {
        // ── PLATEFORME : dalle plate, STABLE (pas de culbute), inclinée fixe pour
        //    qu'on la voie de biais. Elle bancle légèrement vers son sens de dérive
        //    — elle « penche » dans son louvoiement, ce qui la rend vivante.
        if (nPlat >= CAP_OBSTACLES) continue;
        // Bancle proportionnel au pas de dérive, borné : la plateforme penche dans
        // son louvoiement. On compose l'inclinaison fixe (biais) et ce roulis.
        let bank = (ob.x[s]! - ob.px[s]!) * 0.05;
        if (bank > 0.4) bank = 0.4;
        else if (bank < -0.4) bank = -0.4;
        _quat.setFromAxisAngle(_axisX, PLATFORM_TILT);
        _tiltQ.setFromAxisAngle(_axisZ, bank);
        _quat.multiply(_tiltQ);
        // Large et plate : un peu plus grande que le disque de collision, fine en Z.
        _scale.set(r * 1.18, r * 1.18, r * 0.34);
        _mat.compose(_pos, _quat, _scale);
        this.platforms.setMatrixAt(nPlat, _mat);
        platCol.array[nPlat * 3] = TINT_PLATFORM[0];
        platCol.array[nPlat * 3 + 1] = TINT_PLATFORM[1];
        platCol.array[nPlat * 3 + 2] = TINT_PLATFORM[2];
        nPlat++;
        continue;
      }

      if (nRock >= CAP_OBSTACLES) continue;
      // ── ROCHER : culbute ANARCHIQUE autour de SON axe, à SA vitesse (parfois à
      //    l'envers) — comme un météore, jamais d'un bloc avec ses voisins.
      _axis.set(this.axis[s * 3]!, this.axis[s * 3 + 1]!, this.axis[s * 3 + 2]!);
      _quat.setFromAxisAngle(_axis, t * this.spinRate[s]!);
      _scale.set(r * this.jitter[s * 3]!, r * this.jitter[s * 3 + 1]!, r * this.jitter[s * 3 + 2]!);
      _mat.compose(_pos, _quat, _scale);
      this.rocks.setMatrixAt(nRock, _mat);

      // Teinte : froide selon la destructibilité, réchauffée par l'usure.
      const destructible = ob.destructible[s] === 1;
      const cr = destructible ? TINT_DESTRUCTIBLE : TINT_INDESTRUCTIBLE;
      let rr = cr[0];
      let gg = cr[1];
      let bb = cr[2];
      if (destructible) {
        const max = ob.maxHp[s]!;
        const wear = max > 0 ? 1 - ob.hp[s]! / max : 0;
        const tw = wear > 0 ? wear * 0.7 : 0;
        rr += (TINT_WEAR[0] - rr) * tw;
        gg += (TINT_WEAR[1] - gg) * tw;
        bb += (TINT_WEAR[2] - bb) * tw;
      }
      rockCol.array[nRock * 3] = rr;
      rockCol.array[nRock * 3 + 1] = gg;
      rockCol.array[nRock * 3 + 2] = bb;
      nRock++;
    }

    this.rocks.count = nRock;
    if (nRock > 0) {
      this.rocks.instanceMatrix.clearUpdateRanges();
      this.rocks.instanceMatrix.addUpdateRange(0, nRock * 16);
      this.rocks.instanceMatrix.needsUpdate = true;
      rockCol.clearUpdateRanges();
      rockCol.addUpdateRange(0, nRock * 3);
      rockCol.needsUpdate = true;
    }
    this.platforms.count = nPlat;
    if (nPlat > 0) {
      this.platforms.instanceMatrix.clearUpdateRanges();
      this.platforms.instanceMatrix.addUpdateRange(0, nPlat * 16);
      this.platforms.instanceMatrix.needsUpdate = true;
      platCol.clearUpdateRanges();
      platCol.addUpdateRange(0, nPlat * 3);
      platCol.needsUpdate = true;
    }
  }

  private syncShards(dt: number): void {
    const col = this.shards.instanceColor!;
    let n = 0;
    for (let s = 0; s < SHARD_CAP && n < SHARD_CAP; s++) {
      let life = this.slife[s]!;
      if (life <= 0) continue;

      if (dt > 0) {
        // Intégration explicite + léger amortissement : la gerbe s'ouvre vite
        // puis s'essouffle.
        this.sx[s] = this.sx[s]! + this.svx[s]! * dt;
        this.sy[s] = this.sy[s]! + this.svy[s]! * dt;
        const damp = 1 - 2.2 * dt > 0 ? 1 - 2.2 * dt : 0;
        this.svx[s] = this.svx[s]! * damp;
        this.svy[s] = this.svy[s]! * damp;
        this.sang[s] = this.sang[s]! + this.svr[s]! * dt;
        life -= dt;
        this.slife[s] = life > 0 ? life : 0;
        if (this.slife[s]! <= 0) continue;
      }

      const k = life / SHARD_LIFE; // 1 → 0 : rétrécit et s'éteint
      _pos.set(this.sx[s]!, this.sy[s]!, 0);
      _quat.setFromAxisAngle(_axisZ, this.sang[s]!);
      _scale.setScalar(this.ssize[s]! * k);
      _mat.compose(_pos, _quat, _scale);
      this.shards.setMatrixAt(n, _mat);
      col.array[n * 3] = this.ember.r * k;
      col.array[n * 3 + 1] = this.ember.g * k;
      col.array[n * 3 + 2] = this.ember.b * k;
      n++;
    }

    this.shards.count = n;
    if (n > 0) {
      this.shards.instanceMatrix.clearUpdateRanges();
      this.shards.instanceMatrix.addUpdateRange(0, n * 16);
      this.shards.instanceMatrix.needsUpdate = true;
      col.clearUpdateRanges();
      col.addUpdateRange(0, n * 3);
      col.needsUpdate = true;
    }
  }
}
